import { QueryResponse } from "~/modules/filters/types";
import { Network } from "../../types";
import { PageHeader } from "~/components/PageHeader";
import { SectionHeader } from "~/components/SectionHeader";
import { RWA_URLS } from "~/consts";
import { Anchor } from "~/components/Anchor";
import { NetworkTable } from "../../components/NetworkTable";
import { DataExportProvider } from "~/modules/downloads/components/DataExportProvider";
import { DownloadButton } from "~/modules/downloads/components/DownloadButton";
import { ExportTableNames } from "~/modules/downloads/types";
import { DEFAULT_NETWORK_LIST_QUERY } from "../../consts";
import { NetFlowsData } from "~/modules/visualization/util/queryNetFlows";
import { VisualizationGrid } from "~/modules/visualization/components/VisualizationGrid";
import { MetricTimeseriesChart } from "~/modules/visualization/components/MetricTimeseriesChart";
import { MetricTypes } from "~/modules/visualization/components/MetricTimeseriesChart/types";
import { TokenTimeseriesGroupBy } from "~/modules/token_timeseries/types";
import { LeagueTablePieChart } from "~/modules/league_tables/components/LeagueTablePieChart";
import {
    LeagueTableGroupTypes,
    LeagueTableTabsByDistributionType,
} from "~/modules/league_tables/types";
import * as Analytics from "../../analytics";
import {
    Props as AggregateDisplayProps,
    AggregateGrid,
} from "~/components/AggregateDisplay";
import { NetFlowsCard } from "~/modules/visualization/components/NetFlowsChart/NetFlowsCard";

// // // //

export function NetworkListPage(props: {
    aggregates: Array<AggregateDisplayProps>;
    listQueryResponse: QueryResponse<Network>;
    netFlows: NetFlowsData;
    netFlowsWithStablecoins: NetFlowsData;
    leagueTableTabs: LeagueTableTabsByDistributionType;
    leagueTableTabsWithoutStablecoins: LeagueTableTabsByDistributionType;
}) {
    const {
        aggregates,
        listQueryResponse,
        netFlows,
        netFlowsWithStablecoins,
        leagueTableTabs,
        leagueTableTabsWithoutStablecoins,
    } = props;

    return (
        <div className="flex flex-col h-full grow">
            <PageHeader
                title="Networks"
                subtitle={
                    <>
                        Analyze key performance indicators across networks
                        powering tokenized real-world assets. Learn more about
                        tokenized RWAs on the{" "}
                        <Anchor
                            openInNewTab
                            href={RWA_URLS.blog}
                            analyticsLabel={"Networks - RWA.xyz Blog Link"}
                            className="underline underline-offset-4"
                            variant="default"
                        >
                            RWA.xyz Research Blog
                        </Anchor>
                        .
                    </>
                }
            />

            <AggregateGrid aggregates={aggregates} />

            <VisualizationGrid className="mt-6">
                <MetricTimeseriesChart
                    title="Network Metrics"
                    analyticsChartName="network-list-metrics"
                    columnSpanConfig={{
                        sm: 12,
                        md: 12,
                        lg: 12,
                        xl: 12,
                        xxl: 12,
                    }}
                    controls={{
                        metrics: {
                            options: [
                                MetricTypes.TotalValue,
                                MetricTypes.AssetHolders,
                                MetricTypes.ActiveAddresses,
                                MetricTypes.TransferVolume,
                                MetricTypes.MintBurnVolume,
                            ],
                        },
                        groupBy: {
                            enabled: false,
                            value: TokenTimeseriesGroupBy.parentNetwork,
                        },
                    }}
                />

                <LeagueTablePieChart
                    title="Network Asset Value"
                    leagueTableTabs={leagueTableTabs}
                    leagueTableTabsWithoutStablecoins={
                        leagueTableTabsWithoutStablecoins
                    }
                    defaultGroupBy={LeagueTableGroupTypes.parentNetworks}
                    colSpans={{
                        sm: 12,
                        md: 12,
                        lg: 12,
                        xl: 6,
                        xxl: 6,
                    }}
                    onChangeIncludeStablecoins={({ includeStablecoins }) => {
                        Analytics.trackPieChartToggleStablecoins({
                            includeStablecoins,
                        });
                    }}
                />

                <NetFlowsCard
                    netFlows={netFlows}
                    netFlowsWithStablecoins={netFlowsWithStablecoins}
                />
            </VisualizationGrid>

            <div className="mt-6">
                <SectionHeader
                    label="Networks"
                    value={`${listQueryResponse.results.length.toLocaleString()} Total`}
                >
                    <DataExportProvider>
                        {({ isLoading, exportCsv }) => (
                            <DownloadButton
                                disabled={isLoading}
                                onClick={() => {
                                    exportCsv({
                                        exportTableName:
                                            ExportTableNames.networks_v4,
                                        query: DEFAULT_NETWORK_LIST_QUERY,
                                    });
                                }}
                            />
                        )}
                    </DataExportProvider>
                </SectionHeader>

                <NetworkTable networks={listQueryResponse} />
            </div>
        </div>
    );
}
